import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { GeoConfig, GeoListItemType } from '../../configs'
import { Company, File } from 'depoto-core/src/entities'
import { CoreService, NotificationService } from '../../services'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
import { FileUtilService } from '../../utils/file-util.service'
import { getObjectDiff, SchemaUtil } from '../../utils'

@Component({
  selector: 'app-company-settings',
  templateUrl: 'company-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanySettingsComponent implements OnInit, CheckDataChanges {
  company: Company
  countries: GeoListItemType[] = []
  loading = true
  submitted = false
  acceptedFileTypes = 'image/jpeg,image/jpg,image/png,image/gif,image/webp'

  companySettingsForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, []),
    ic: new FormControl({ value: '', disabled: false }, []),
    dic: new FormControl({ value: '', disabled: false }, []),
    phone: new FormControl({ value: '', disabled: false }, []),
    email: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    street: new FormControl({ value: '', disabled: false }, []),
    city: new FormControl({ value: '', disabled: false }, []),
    zip: new FormControl({ value: '', disabled: false }, []),
    country: new FormControl({ value: '', disabled: false }, [Validators.required]),
    registrationNote: new FormControl({ value: '', disabled: false }, []),
    nextEan: new FormControl({ value: null, disabled: false }, []),
    nextCode: new FormControl({ value: null, disabled: false }, []),
    defaultVoucherValidity: new FormControl({ value: 6, disabled: false }, []),
    logo: new FormControl({ value: null, disabled: false }, []),
  })

  constructor(
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private fileUtilService: FileUtilService
  ) {
    this.countries = GeoConfig.getCountries(this.translateService.currentLang)
    this.translateService.onLangChange.subscribe(() => {
      this.countries = GeoConfig.getCountries(this.translateService.currentLang)
    })
  }

  ngOnInit() {
    this.loading = true
    const company = this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
    console.warn(company)
    if (company && company.id > 0) {
      this.company = company
      this.fillFormValue()
      this.loading = false
    }
  }

  hasUnsavedDataChanges(): boolean {
    const originCompany = structuredClone(this.company)
    delete originCompany.id
    delete originCompany.billLogo
    delete originCompany.parent
    delete originCompany.carrierRelations
    delete originCompany.moduleRelations
    delete originCompany.currency
    delete originCompany.batchExpirationMode
    delete originCompany.children
    delete originCompany.tariff
    delete originCompany.tariffExpirationDate
    delete originCompany.enabledLocking

    const editedCompany = structuredClone(this.companySettingsForm.value)
    const diff = getObjectDiff(structuredClone(editedCompany), structuredClone(originCompany))
    if (!diff) {
      return
    }
    return !!diff.filter(d => d !== 'files').length
  }

  public fillFormValue() {
    this.companySettingsForm.setValue({
      name: this.company.name,
      ic: this.company.ic,
      dic: this.company.dic,
      phone: this.company.phone,
      email: this.company.email,
      street: this.company.street,
      city: this.company.city,
      zip: this.company.zip,
      country: this.company.country,
      registrationNote: this.company.registrationNote,
      nextEan: this.company.nextEan,
      nextCode: this.company.nextCode || null,
      defaultVoucherValidity: this.company.defaultVoucherValidity || 6,
      logo: this.company.logo || null,
    })
  }

  public getFormValue() {
    this.company.name = this.companySettingsForm.value.name
    this.company.ic = this.companySettingsForm.value.ic
    this.company.dic = this.companySettingsForm.value.dic
    this.company.phone = this.companySettingsForm.value.phone
    this.company.email = this.companySettingsForm.value.email
    this.company.street = this.companySettingsForm.value.street
    this.company.city = this.companySettingsForm.value.city
    this.company.zip = this.companySettingsForm.value.zip
    this.company.country = this.companySettingsForm.value.country
    this.company.registrationNote = this.companySettingsForm.value.registrationNote
    this.company.nextEan = this.companySettingsForm.value.nextEan
    this.company.nextCode = this.companySettingsForm.value.nextCode
    this.company.defaultVoucherValidity = this.companySettingsForm.value.defaultVoucherValidity
    this.company.logo = this.companySettingsForm.value.logo
  }

  async updateCompany() {
    this.submitted = true
    if (this.companySettingsForm.invalid) {
      return
    }
    try {
      this.loading = true

      if (
        !!this.company.logo?.id &&
        this.companySettingsForm.value.logo?.id &&
        this.company.logo?.id !== this.companySettingsForm.value.logo?.id
      ) {
        await this.removeLogo(new File(this.company.logo), true)
      }

      this.getFormValue()
      await this.core.services.company.update(this.company).then(() => {
        this.notificationService.success(this.translateService.instant('alert.company.updated'))
        this.core.services.storage.set(this.core.services.storage.keys.auth.company, this.company)
        this.fillFormValue()
      })
    } catch (err) {
      console.error(err)
    } finally {
      this.loading = false
    }
  }
  get formControls(): { [key: string]: AbstractControl } {
    return this.companySettingsForm.controls
  }

  async onSelect(event) {
    this.loading = true
    const enabledMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    // todo change to just check
    if (!enabledMimeTypes.includes(event.addedFiles[0]?.type)) {
      // todo alert
      this.loading = false
      return
    }
    try {
      const processedFile = await this.fileUtilService.processUploadedFileToBase64(event.addedFiles[0])
      if (processedFile?.base64Data) {
        this.companySettingsForm.value.logo = await this.core.services.file
          .create(processedFile, null, null, null, null, null, SchemaUtil.file)
          .then(result => {
            if (result?.id) {
              this.notificationService.success(this.translateService.instant('alert.company.logo.added'))
              return result
            } else {
              this.notificationService.error(this.translateService.instant('no-files'))
            }
          })
      }
    } catch (err) {
      console.warn(err)
    } finally {
      this.loading = false
    }
  }

  async removeLogo(image: File | Partial<File>, deleteFromUpdate = false) {
    if (!deleteFromUpdate && !confirm(this.translateService.instant('delete.confirmation'))) {
      return
    }
    try {
      await this.core.services.file.delete(image).then(res => {
        this.notificationService.success(this.translateService.instant('alert.image.deleted'))
      })
      if (!deleteFromUpdate) {
        this.company.logo = null
        this.companySettingsForm.value.logo = null
      }
    } catch (err) {
      console.error(err)
    }
  }

  getThumbUrl(image: File): string {
    return image?.thumbnails?.filter(t => t.format === 'w135wp')[0]?.url
    // this.imageUrl = this.image.thumbnails.filter(t => t.format === 'w2048wp')[0].url
    // this.label = this.image.text
  }
}
